/**
 * SplitDialog — Token split dialog with toggle switches.
 *
 * Ported from test-beta-6.py showSplitDialog / applySplit logic.
 * Auto-detects separator, shows toggle switches between word segments,
 * and supports batch split for multiple selected tokens.
 * Uses orphan patterns to intelligently suggest split points for known field types like rooms.
 */
import { useState, useMemo, useEffect, useRef } from 'react'
import { Button } from '@/components/common/Button'
import { isIgnoredToken, loadIgnoredTokenNames } from '@/utils/ignoredTokens'
import { loadSplitHints } from '@/utils/loadSplitHints'
import { characterTones, EMPTY_SPLIT_HINTS, suggestCuts, withConfirmed, type ConfirmedToken, type SplitHints } from '@/utils/splitSuggestion'
import { splitAtCuts } from '@/utils/tokenSplit'
import { CharacterRuler } from './CharacterRuler'

interface SplitDialogProps {
  isOpen: boolean
  token: string
  selectedTokens: string[]
  orphanPatterns?: Array<{ regex: string; field: string; match_mode: string }>
  hints?: SplitHints
  confirmed?: ConfirmedToken[]
  onApply: (parts: string[], splitIndices: number[], separator: string, editedToken: string) => void
  onCancel: () => void
}

/**
 * Detect the most likely separator for a token.
 * Returns space if token contains whitespace, otherwise the most frequent special char.
 */
function detectSeparator(token: string): string {
  if (/\s/.test(token)) return ' '
  const sepCounts: Record<string, number> = {}
  const seps = token.match(/[-_./\\]/g)
  if (seps) {
    for (const s of seps) {
      sepCounts[s] = (sepCounts[s] || 0) + 1
    }
    let maxCount = 0
    let maxSep = '-'
    for (const s in sepCounts) {
      if (sepCounts[s]! > maxCount) {
        maxCount = sepCounts[s]!
        maxSep = s
      }
    }
    return maxSep
  }
  return ' '
}

/**
 * Check if a token has valid split points.
 */
export function hasSplitPoints(token: string): boolean {
  const isTimeSlot = /^\d{1,2}:\d{2}[-–]?\d{1,2}:\d{2}$/.test(token)
  const isSingleLetter = /^[A-Za-z]$/.test(token)
  const hasSep = /\s/.test(token) || /[-_./\\]/.test(token)
  return hasSep && !isTimeSlot && !isSingleLetter
}

export function SplitDialog({ isOpen, token, selectedTokens, hints: hintsProp, confirmed, onApply, onCancel }: SplitDialogProps) {
  const [toggles, setToggles] = useState<Record<number, boolean>>({})
  const [editText, setEditText] = useState(token)
  const [ignoredNames, setIgnoredNames] = useState<string[]>([])
  const [loadedHints, setLoadedHints] = useState<SplitHints>(EMPTY_SPLIT_HINTS)
  const [rulerCuts, setRulerCuts] = useState<number[]>([])
  const hints = useMemo(
    () =>
      withConfirmed(hintsProp ?? loadedHints, [
        ...(confirmed || []),
        ...ignoredNames.map((text) => ({ text, field: 'ignore' })),
      ]),
    [hintsProp, loadedHints, confirmed, ignoredNames],
  )
  const togglesTouched = useRef(false)
  const rulerTouched = useRef(false)

  useEffect(() => {
    if (!isOpen || hintsProp) return
    let cancelled = false
    loadSplitHints()
      .then((next) => {
        if (!cancelled) setLoadedHints(next)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isOpen, hintsProp])

  useEffect(() => {
    if (isOpen) {
      setEditText(token)
      setToggles({})
      let cancelled = false
      loadIgnoredTokenNames()
        .then((names) => {
          if (!cancelled) setIgnoredNames((prev) => (prev.join('\n') === names.join('\n') ? prev : names))
        })
        .catch(() => {
          if (!cancelled) setIgnoredNames((prev) => (prev.length === 0 ? prev : []))
        })
      return () => {
        cancelled = true
      }
    }
    return undefined
  }, [isOpen, token])

  const separator = useMemo(() => detectSeparator(editText), [editText])
  const semanticCuts = useMemo(() => suggestCuts(editText, hints), [editText, hints])

  const words = useMemo(() => {
    if (!editText) return []
    const escapedSep = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return editText.split(new RegExp(escapedSep + '+')).filter(w => w)
  }, [editText, separator])

  useEffect(() => {
    togglesTouched.current = false
    rulerTouched.current = false
  }, [editText])

  useEffect(() => {
    if (!isOpen || words.length < 2 || togglesTouched.current) {
      if (!isOpen || words.length < 2) setToggles({})
      return
    }
    const escapedSep = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const starts: number[] = []
    const re = new RegExp(escapedSep + '+', 'g')
    let match: RegExpExecArray | null
    while ((match = re.exec(editText))) starts.push(match.index)
    const initial: Record<number, boolean> = {}
    for (let i = 0; i < words.length - 1; i++) {
      initial[i] = starts[i] !== undefined && semanticCuts.includes(starts[i]!)
    }
    setToggles(initial)
  }, [isOpen, editText, separator, words.length, semanticCuts])
  useEffect(() => {
    if (!rulerTouched.current) setRulerCuts(semanticCuts)
  }, [semanticCuts])
  const semanticParts = useMemo(
    () => (words.length < 2 ? splitAtCuts(editText, rulerCuts) : []),
    [editText, rulerCuts, words.length],
  )

  const preview = useMemo(() => {
    if (words.length < 2) return semanticParts.length >= 2 ? semanticParts : []
    const result: string[] = []
    let current = words[0] || ''
    for (let i = 0; i < words.length - 1; i++) {
      if (toggles[i]) {
        result.push(current)
        current = words[i + 1] || ''
      } else {
        current += separator + (words[i + 1] || '')
      }
    }
    result.push(current)
    return result
  }, [words, toggles, separator, semanticParts])

  // Count how many selected tokens can be split (have >= 2 words)
  const batchInfo = useMemo(() => {
    if (selectedTokens.length <= 1) return null
    const otherTokens = selectedTokens.filter(t => t !== token)
    let matching = 0
    let skipped = 0
    for (const otherToken of otherTokens) {
      const otherSep = detectSeparator(otherToken)
      const otherEscapedSep = otherSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const otherWords = otherToken.split(new RegExp(otherEscapedSep + '+')).filter(w => w)
      if (otherWords.length >= 2) {
        matching++
      } else {
        skipped++
      }
    }
    return { matching, skipped, total: otherTokens.length }
  }, [selectedTokens, token])

  const handleToggle = (idx: number) => {
    togglesTouched.current = true
    setToggles(prev => ({ ...prev, [idx]: !prev[idx] }))
  }

  const handleApply = () => {
    const splitIndices = Object.entries(toggles)
      .filter(([, on]) => on)
      .map(([idx]) => parseInt(idx, 10))
    const validParts = preview.filter((p): p is string => p !== undefined && p.length > 0)
    onApply(validParts, splitIndices, separator, editText)
  }

  if (!isOpen) return null

  const validParts = preview.filter(p => p)
  const canApply = validParts.length >= 2 && editText.trim().length > 0

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={onCancel}>
      <div
        className="bg-white rounded-lg p-6 max-w-lg w-full mx-4"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="font-medium text-lg mb-2">Token teilen</h3>
        {hints.failed?.length ? (
          <p className="mb-2 text-xs text-amber-700">{hints.failed.join(', ')} konnten nicht geladen werden.</p>
        ) : null}
        <input
          type="text"
          value={editText}
          onChange={e => setEditText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && canApply) { e.preventDefault(); handleApply() }
            if (e.key === 'Escape') { e.preventDefault(); onCancel() }
          }}
          className="w-full bg-gray-100 px-3 py-2 rounded text-center font-mono text-base border border-gray-300 focus:border-blue-500 focus:outline-none"
          placeholder="Token bearbeiten..."
          autoFocus
        />
        {editText !== token && (
          <div className="text-xs text-gray-400 text-center mt-1">
            Original: <code className="font-mono">{token}</code>
          </div>
        )}

        {editText.length > 1 ? (
          <div className="mb-4">
            <CharacterRuler
              token={editText}
              cuts={rulerCuts}
              tones={characterTones(editText, rulerCuts, hints, ignoredNames)}
              onChange={(next) => {
                rulerTouched.current = true
                setRulerCuts(next)
              }}
            />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg mb-4">
          {words.map((word, i) => (
            <span key={i} className="font-mono text-sm px-2 py-1 bg-white border border-gray-200 rounded">
              {word}
            </span>
          ))}
          {words.length > 1 && words.slice(0, -1).map((_, i) => (
            <button
              key={`toggle-${i}`}
              className={`w-10 h-6 rounded-full border-none cursor-pointer relative transition-colors ${
                toggles[i] ? 'bg-green-500' : 'bg-gray-300'
              }`}
              onClick={() => handleToggle(i)}
              aria-label={`Toggle split after "${words[i]}"`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                  toggles[i] ? 'translate-x-4' : ''
                }`}
              />
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="mb-4 p-2 bg-green-50 rounded text-sm">
          <span className="text-gray-600">Ergebnis: </span>
          <span className="font-mono font-medium">
            {validParts.map((part, index) => (
              <span key={`${part}-${index}`}>
                {index > 0 ? ' | ' : ''}
                <span className={isIgnoredToken(part, ignoredNames) ? 'text-gray-400 line-through' : ''}>{part}</span>
                {isIgnoredToken(part, ignoredNames) ? <span className="ml-1 text-[10px] text-gray-500 no-underline">Ignorieren</span> : null}
              </span>
            ))}
          </span>
        </div>

        {/* Batch info */}
        {batchInfo && batchInfo.total > 0 && (
          <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700">
            {batchInfo.matching > 0 && (
              <span>{batchInfo.matching} weitere Token werden gleich geteilt. </span>
            )}
            {batchInfo.skipped > 0 && (
              <span className="text-amber-600">{batchInfo.skipped} Token übersprungen (andere Struktur).</span>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleApply}
            disabled={!canApply}
          >
            Teilen
          </Button>
          <Button variant="secondary" className="flex-1" onClick={onCancel}>
            Abbrechen
          </Button>
        </div>
      </div>
    </div>
  )
}
